import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";
import { CustomInput } from "../atoms/CustomInput";
import { CustomButton } from "../atoms/CustomButton";
import { useCreateNewKeyMutation } from "../../hooks/users";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { queryClient } from "../../providers/QueryProvider";
import { USER_NOTES } from "../../constants/types";
import { CharacterCounter } from "../atoms/CharacterCounter";
import { ModalShell } from "../atoms/ModalShell";

interface AddNoteModalProps {
  showModal: boolean;
  setShowModal: (v: boolean) => void;
  editData?: any;
}

export default function AddNoteModal({
  showModal,
  setShowModal,
  editData,
}: AddNoteModalProps) {
  const [t] = useTranslation();

  const schema = useMemo(
    () =>
      Joi.object({
        note: Joi.string()
          .trim()
          .min(2)
          .max(3500)
          .required()
          .label("Note")
          .messages({
            "string.empty": t(
              "dashboard.entities.viewEntityUser.key30"
            ) as string,
            "any.required": t(
              "dashboard.entities.viewEntityUser.key30"
            ) as string,
            "string.min": t(
              "dashboard.entities.viewEntityUser.key31"
            ) as string,
          }),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: joiResolver(schema),
  });
  const { user, customer_identification } = useAuth();
  const { user_identification } = useParams();

  const { mutate: createKey, isLoading } = useCreateNewKeyMutation();

  useEffect(() => {
    if (editData) {
      reset({
        note: editData.value[0].note || "",
      });
    }
  }, [editData]);

  const onSubmit = useCallback(
    (values: any) => {
      const existingNotes =
        queryClient.getQueryData<any>(["getUserData", user_identification])
          ?.user_data[USER_NOTES] || [];

      const newNotes = editData
        ? existingNotes.map((note: any) =>
            note.key === editData.key
              ? {
                  ...note,
                  value: [{ note: values.note }],
                }
              : note
          )
        : [
            ...existingNotes,
            {
              key: new Date().toISOString(),
              value: [{ ...values }],
            },
          ];

      createKey(
        {
          customer_identification: customer_identification,
          entityIdentification: user.entity_identification,
          customerId: user_identification,
          key: USER_NOTES,
          value: newNotes,
        },
        {
          onSuccess: () => {
            reset();
            queryClient.invalidateQueries(["getUserData", user_identification]);
            setShowModal(false);
          },
        }
      );
    },
    [
      user_identification,
      editData,
      createKey,
      customer_identification,
      user.entity_identification,
      reset,
      setShowModal,
    ]
  );

  return (
    <ModalShell
      show={showModal}
      onClose={() => setShowModal(false)}
      title={
        editData
          ? t("dashboard.entities.viewEntityUser.key25")
          : t("dashboard.entities.viewEntityUser.key19")
      }
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <CustomButton
          type="submit"
          disabled={isLoading}
          isLoading={isLoading}
          variant="primary"
          title={
            editData
              ? t("dashboard.entities.viewEntityUser.key25")
              : (t("common.add") as string)
          }
        />
      }
    >
      <CustomInput
        name="note"
        control={control}
        rows={6}
        maxLength={3500}
        error={errors.note}
        helperText={errors.note?.message}
        label={t("dashboard.entities.viewEntityUser.key26") as string}
        placeholder={t("dashboard.entities.viewEntityUser.key27") as string}
      />
      <CharacterCounter
        currentLength={watch("note")?.length || 0}
        maxLength={3500}
      />
    </ModalShell>
  );
}
